import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Button,
  Link as MuiLink,
  TextField,
  InputAdornment,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import type { TTextilePurchase } from "@/api/customs/textiles";
import * as XLSX from "xlsx";
import { PURCHASES } from "@/config/resources";

interface DetailTableProps {
  data: TTextilePurchase[];
}

type FilterKey = "kam" | "status" | "payment_method" | "payment_status";

interface Filters {
  kam: string[];
  status: string[];
  payment_method: string[];
  payment_status: string[];
}

export const DetailTable = ({ data }: DetailTableProps) => {
  const [filters, setFilters] = useState<Filters>({
    kam: [],
    status: [],
    payment_method: [],
    payment_status: [],
  });

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [anchorEl, setAnchorEl] = useState<Record<FilterKey, HTMLElement | null>>({
    kam: null,
    status: null,
    payment_method: null,
    payment_status: null,
  });

  // Get unique values for each filter
  const filterOptions = useMemo(() => {
    return {
      kam: Array.from(new Set(data.map((item) => item.kam))).sort(),
      status: Array.from(new Set(data.map((item) => item.status))).sort(),
      payment_method: Array.from(
        new Set(data.map((item) => item.payment_method))
      ).sort(),
      payment_status: Array.from(
        new Set(data.map((item) => item.payment_status))
      ).sort(),
    };
  }, [data]);

  // Filter data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Búsqueda por número de pedido
      if (
        searchQuery &&
        !item.purchase_number.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }
      
      if (filters.kam.length > 0 && !filters.kam.includes(item.kam)) {
        return false;
      }
      if (filters.status.length > 0 && !filters.status.includes(item.status)) {
        return false;
      }
      if (
        filters.payment_method.length > 0 &&
        !filters.payment_method.includes(item.payment_method)
      ) {
        return false;
      }
      if (
        filters.payment_status.length > 0 &&
        !filters.payment_status.includes(item.payment_status)
      ) {
        return false;
      }
      return true;
    });
  }, [data, filters, searchQuery]);

  const handleFilterClick = (
    filterKey: FilterKey,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl((prev) => ({ ...prev, [filterKey]: event.currentTarget }));
  };

  const handleFilterClose = (filterKey: FilterKey) => {
    setAnchorEl((prev) => ({ ...prev, [filterKey]: null }));
  };

  const handleFilterToggle = (filterKey: FilterKey, value: string) => {
    setFilters((prev) => {
      const currentFilter = prev[filterKey];
      const newFilter = currentFilter.includes(value)
        ? currentFilter.filter((v) => v !== value)
        : [...currentFilter, value];
      return { ...prev, [filterKey]: newFilter };
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getAppliedFiltersText = () => {
    const appliedFilters: string[] = [];
    const filterLabels: Record<FilterKey, string> = {
      kam: "KAM",
      status: "Status Pedido",
      payment_method: "Método de Pago",
      payment_status: "Status Pago",
    };

    if (searchQuery) {
      appliedFilters.push(`Búsqueda: "${searchQuery}"`);
    }

    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        appliedFilters.push(filterLabels[key as FilterKey]);
      }
    });

    if (appliedFilters.length === 0) {
      return "Sin filtros aplicados";
    }

    return `Filtros aplicados: ${appliedFilters.join(", ")}`;
  };

  const handleExportToExcel = () => {
    const exportData = filteredData.map((item) => ({
      Pedido: item.purchase_number,
      KAM: item.kam,
      "Método de Pago": item.payment_method,
      "Status Pedido": item.status,
      "Status Pago": item.payment_status,
      "Revenue con IVA": item.revenue_con_iva,
      "Revenue sin IVA": item.revenue_sin_iva,
    }));

    // Add filters info at the end
    const filtersText = getAppliedFiltersText();
    exportData.push({
      Pedido: "",
      KAM: "",
      "Método de Pago": "",
      "Status Pedido": "",
      "Status Pago": "",
      "Revenue con IVA": "" as any,
      "Revenue sin IVA": "" as any,
    });
    exportData.push({
      Pedido: filtersText,
      KAM: "",
      "Método de Pago": "",
      "Status Pedido": "",
      "Status Pago": "",
      "Revenue con IVA": "" as any,
      "Revenue sin IVA": "" as any,
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Textiles");

    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `textiles_${date}.xlsx`);
  };

  const FilterButton = ({
    filterKey,
    label,
  }: {
    filterKey: FilterKey;
    label: string;
  }) => {
    const hasActiveFilter = filters[filterKey].length > 0;

    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => handleFilterClick(filterKey, e)}
          color={hasActiveFilter ? "primary" : "default"}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl[filterKey]}
          open={Boolean(anchorEl[filterKey])}
          onClose={() => handleFilterClose(filterKey)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableScrollLock={true}
          PaperProps={{
            style: {
              maxHeight: 300,
              minWidth: 200,
            },
          }}
        >
          {filterOptions[filterKey].map((option) => (
            <MenuItem
              key={option}
              onClick={() => handleFilterToggle(filterKey, option)}
              dense
            >
              <Checkbox
                checked={filters[filterKey].includes(option)}
                size="small"
              />
              <ListItemText primary={option} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <TextField
          placeholder="Buscar por número de pedido..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
          color="primary"
        >
          Descargar Excel
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Pedido
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    KAM
                  </Typography>
                  <FilterButton filterKey="kam" label="KAM" />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Método de Pago
                  </Typography>
                  <FilterButton
                    filterKey="payment_method"
                    label="Método de Pago"
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Status Pedido
                  </Typography>
                  <FilterButton filterKey="status" label="Status Pedido" />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Status Pago
                  </Typography>
                  <FilterButton
                    filterKey="payment_status"
                    label="Status Pago"
                  />
                </Box>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  Revenue con IVA
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  Revenue sin IVA
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((purchase) => (
              <TableRow key={purchase.id}>
                <TableCell>
                  <MuiLink
                    href={`/${PURCHASES}/${purchase.id}/show`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {purchase.purchase_number}
                  </MuiLink>
                </TableCell>
                <TableCell>{purchase.kam}</TableCell>
                <TableCell>{purchase.payment_method}</TableCell>
                <TableCell>{purchase.status}</TableCell>
                <TableCell>{purchase.payment_status}</TableCell>
                <TableCell align="right">
                  {formatCurrency(purchase.revenue_con_iva)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(purchase.revenue_sin_iva)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No hay datos que coincidan con los filtros seleccionados
          </Typography>
        </Box>
      )}
    </Box>
  );
};

